import { motion } from 'framer-motion';
import { Button } from '@nextui-org/react';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useFirestore } from 'reactfire';
import { collection, addDoc } from 'firebase/firestore';
import { FiFileText, FiEdit, FiMessageSquare, FiCheck, FiCode, FiFile, FiDatabase } from 'react-icons/fi';

const Landing = () => {
  const navigate = useNavigate();
  const firestore = useFirestore();
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const features = [
    {
      icon: <FiFileText className="w-12 h-12" />,
      title: 'DoxEx',
      description: 'Easily extract data into structured format from unstructured documents. Transform complex PDFs, images, and spreadsheets into clean, usable data with AI-powered extraction.',
      color: 'from-blue-500 to-cyan-500',
      benefits: ['AI-powered extraction', 'Custom field mapping', 'Batch processing']
    },
    {
      icon: <FiCode className="w-12 h-12" />,
      title: 'DoxComp',
      description: 'Compare two documents and get accurate differences based on data, not just text. Identify changes in contracts, invoices, and reports with precision.',
      color: 'from-purple-500 to-pink-500',
      benefits: ['Smart comparison', 'Highlight differences', 'Export reports']
    },
    {
      icon: <FiEdit className="w-12 h-12" />,
      title: 'DoxGen',
      description: 'Generate new documents with data extracted from existing documents. Create templates and automatically populate them with extracted information.',
      color: 'from-orange-500 to-red-500',
      benefits: ['Template creation', 'Auto-population', 'Multi-format output']
    },
    {
      icon: <FiMessageSquare className="w-12 h-12" />,
      title: 'DoxBench',
      description: 'A workbench to chat with your documents and get instant insights. Ask questions, search across documents, and get AI-powered answers.',
      color: 'from-green-500 to-teal-500',
      benefits: ['Natural language queries', 'Cross-document search', 'Instant insights']
    },
    {
      icon: <FiDatabase className="w-12 h-12" />,
      title: 'DoxBase',
      description: 'Manage your document knowledge base with ease. Organize, categorize, and maintain your document collections in a centralized repository.',
      color: 'from-teal-500 to-emerald-500',
      benefits: ['Centralized repository', 'Attach to jobs']
    },
    {
      icon: <FiFile className="w-12 h-12" />,
      title: 'Multi-Format Support',
      description: 'Support for multiple document formats including TIFF, JPG, PNG, PDF, XLS, XLSX, DOCX, and more. Process any document type seamlessly.',
      color: 'from-indigo-500 to-blue-500',
      benefits: ['TIFF, JPG, PNG images', 'PDF documents', 'Excel & Word files']
    }
  ];

  const industries = [
    'Healthcare',
    'Retail',
    'Legal',
    'Insurance',
    'Finance',
    'Real Estate'
  ];

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await addDoc(collection(firestore, 'contact_requests'), {
        ...contactForm,
        timestamp: new Date(),
        status: 'new'
      });

      setSubmitSuccess(true);
      setContactForm({ name: '', email: '', company: '', message: '' });

      setTimeout(() => setSubmitSuccess(false), 5000);
    } catch (error) {
      console.error('Error submitting contact form:', error);
      alert('Error submitting form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-gray-900 overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center px-4 py-20">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-to-br from-blue-500/5 to-purple-500/5 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-to-br from-cyan-500/5 to-pink-500/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="relative z-10 max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-6xl md:text-8xl font-bold mb-6 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
              DoxPro.ai
            </h1>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl md:text-2xl mb-8 text-gray-700 max-w-3xl mx-auto"
          >
            Transform unstructured documents into structured data. Consume, compare, interact, and reason with your documents like never before.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex gap-4 justify-center flex-wrap"
          >
            <Button
              size="lg"
              color="primary"
              variant="shadow"
              className="text-lg px-8 py-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:scale-105 transition-transform"
              onClick={() => navigate('/signup')}
            >
              Get Started
            </Button>
            <Button
              size="lg"
              variant="bordered"
              className="text-lg px-8 py-6 border-gray-300 text-gray-700 hover:border-gray-500 hover:scale-105 transition-transform"
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Contact Sales
            </Button>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.6 }}
          className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
        >
          <div className="animate-bounce">
            <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </div>
        </motion.div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 relative bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-20"
          >
            <h2 className="text-5xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Powerful Features
            </h2>
            <p className="text-xl text-gray-600">Everything you need to work with documents efficiently</p>
          </motion.div>

          <div className="space-y-32">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-12 items-center`}
              >
                {/* Image/Illustration Side */}
                <motion.div
                  className="flex-1 w-full"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className={`relative rounded-2xl overflow-hidden bg-gradient-to-br ${feature.color} p-1`}>
                    <div className="bg-white rounded-xl p-12 aspect-video flex items-center justify-center">
                      <div className={`w-32 h-32 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center text-white transform transition-transform hover:rotate-6`}>
                        {feature.icon}
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Content Side */}
                <div className="flex-1 w-full">
                  <motion.div
                    initial={{ opacity: 0, x: index % 2 === 0 ? 30 : -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                  >
                    <div className={`inline-block px-4 py-2 rounded-full bg-gradient-to-r ${feature.color} text-white text-sm font-semibold mb-4`}>
                      {feature.title}
                    </div>
                    <h3 className="text-3xl font-bold mb-4 text-gray-900">
                      {feature.title === 'DoxEx' && 'Extract Data Effortlessly'}
                      {feature.title === 'DoxComp' && 'Compare with Precision'}
                      {feature.title === 'DoxGen' && 'Generate Documents Automatically'}
                      {feature.title === 'DoxBench' && 'Chat with Your Documents'}
                      {feature.title === 'DoxBase' && 'Manage Your Knowledge Base'}
                      {feature.title === 'Multi-Format Support' && 'Work with Any File Type'}
                    </h3>
                    <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                      {feature.description}
                    </p>
                    <ul className="space-y-3">
                      {feature.benefits.map((benefit, idx) => (
                        <motion.li
                          key={idx}
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.4, delay: 0.4 + (idx * 0.1) }}
                          className="flex items-center gap-3 text-gray-700"
                        >
                          <div className={`w-6 h-6 rounded-full bg-gradient-to-r ${feature.color} flex items-center justify-center flex-shrink-0`}>
                            <FiCheck className="w-4 h-4 text-white" />
                          </div>
                          <span>{benefit}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl font-bold mb-4 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              Industries We Serve
            </h2>
            <p className="text-xl text-gray-600">Trusted across multiple sectors</p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6"
          >
            {industries.map((industry, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                whileHover={{ scale: 1.1, rotate: 2 }}
                className="bg-gradient-to-br from-gray-50 to-gray-100 p-6 rounded-xl border border-gray-200 hover:border-purple-400 hover:shadow-lg transition-all text-center"
              >
                <p className="text-lg font-semibold text-gray-900">{industry}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="text-5xl font-bold mb-8 bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
              Pricing
            </h2>

            <motion.div
              whileHover={{ scale: 1.02 }}
              className="bg-white p-12 rounded-3xl border border-gray-200 hover:border-orange-400 hover:shadow-xl transition-all"
            >
              <h3 className="text-3xl font-bold mb-4 text-gray-900">Custom Pricing</h3>
              <p className="text-xl text-gray-600 mb-8">
                Tailored to your needs based on number of documents, pages, and usage requirements
              </p>
              <Button
                size="lg"
                className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-8 py-6 text-lg hover:scale-105 transition-transform"
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Contact Us for Pricing
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-5xl font-bold mb-4 text-center bg-gradient-to-r from-green-600 to-teal-600 bg-clip-text text-transparent">
              Contact Us
            </h2>
            <p className="text-xl text-gray-600 text-center mb-12">
              Get in touch to learn more about how DoxPro.ai can transform your document workflow
            </p>

            {submitSuccess && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-green-50 border border-green-400 rounded-lg flex items-center gap-2 text-green-700"
              >
                <FiCheck className="w-5 h-5" />
                <p>Thank you! We'll get back to you soon.</p>
              </motion.div>
            )}

            <motion.form
              onSubmit={handleContactSubmit}
              className="bg-gray-50 p-8 rounded-3xl border border-gray-200 shadow-lg"
            >
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">Name *</label>
                  <input
                    type="text"
                    required
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-colors text-gray-900"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">Email *</label>
                  <input
                    type="email"
                    required
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-colors text-gray-900"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium mb-2 text-gray-700">Company</label>
                <input
                  type="text"
                  value={contactForm.company}
                  onChange={(e) => setContactForm({ ...contactForm, company: e.target.value })}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-colors text-gray-900"
                  placeholder="Your company"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium mb-2 text-gray-700">Message *</label>
                <textarea
                  required
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  rows={5}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-colors resize-none text-gray-900"
                  placeholder="Tell us about your document processing needs..."
                />
              </div>

              <Button
                type="submit"
                size="lg"
                isLoading={isSubmitting}
                className="w-full bg-gradient-to-r from-green-500 to-teal-500 text-white text-lg py-6 hover:scale-105 transition-transform"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </Button>
            </motion.form>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-gray-200 bg-gray-50">
        <div className="max-w-7xl mx-auto text-center text-gray-600">
          <p>&copy; 2025 DoxPro.ai - Transform your documents with AI</p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
